/**
 * Immersive Theme Context
 * 
 * Provides immersive theme state and actions to the entire application.
 * Handles CSS Variables injection, persistence, and accessibility settings.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuthStore } from '../stores/auth.store';
import {
  ImmersiveThemeType,
  ImmersiveThemeContextValue,
  ImmersiveThemeResponse,
  ThemePreset,
  ThemeConfig,
  UpdateImmersiveThemeDto,
  AllThemePresetsResponse,
} from '../types/immersive-theme.types';

// ============================================
// CONTEXT
// ============================================

const ImmersiveThemeContext = createContext<ImmersiveThemeContextValue | null>(null);

// ============================================
// CSS VARIABLES INJECTION
// ============================================

const applyCssVariables = (variables: Record<string, string>, themeType: ImmersiveThemeType) => {
  const root = document.documentElement;
  
  // Remove previous theme variables
  const existingVars = Array.from(root.style).filter(
    prop => prop.startsWith('--neu-') || 
            prop.startsWith('--brut-') || 
            prop.startsWith('--term-') || 
            prop.startsWith('--org-') || 
            prop.startsWith('--lux-') ||
            prop.startsWith('--animation-') ||
            prop.startsWith('--transition-')
  );
  existingVars.forEach(prop => root.style.removeProperty(prop));
  
  // Apply new variables
  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  
  // Set data attribute for CSS selectors
  root.setAttribute('data-immersive-theme', themeType);
};

const clearCssVariables = () => {
  const root = document.documentElement;
  
  const existingVars = Array.from(root.style).filter(
    prop => prop.startsWith('--neu-') || 
            prop.startsWith('--brut-') || 
            prop.startsWith('--term-') || 
            prop.startsWith('--org-') || 
            prop.startsWith('--lux-') ||
            prop.startsWith('--animation-') ||
            prop.startsWith('--transition-')
  );
  existingVars.forEach(prop => root.style.removeProperty(prop));
  
  root.setAttribute('data-immersive-theme', 'NONE');
};

// ============================================
// PROVIDER
// ============================================

interface ImmersiveThemeProviderProps {
  children: React.ReactNode;
}

export const ImmersiveThemeProvider: React.FC<ImmersiveThemeProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  // Preview state (local, not persisted)
  const [previewState, setPreviewState] = useState<{
    theme: ImmersiveThemeType;
    config: ThemeConfig | null;
    cssVariables: Record<string, string>;
  } | null>(null);

  // Detect system preference for reduced motion
  const [systemReducedMotion, setSystemReducedMotion] = useState(
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setSystemReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // ============================================
  // API QUERIES
  // ============================================

  // Fetch current theme
  const { data: themeData, isLoading: isLoadingTheme } = useQuery<ImmersiveThemeResponse>({
    queryKey: ['immersive-theme'],
    queryFn: async () => {
      const response = await api.get('/theming/immersive');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    enabled: isAuthenticated, // Only fetch when authenticated
  });

  // Fetch all presets
  const { data: presetsData } = useQuery<AllThemePresetsResponse>({
    queryKey: ['immersive-theme-presets'],
    queryFn: async () => {
      const response = await api.get('/theming/immersive/presets');
      return response.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    enabled: isAuthenticated, // Only fetch when authenticated
  });

  // ============================================
  // MUTATIONS
  // ============================================

  const updateThemeMutation = useMutation({
    mutationFn: async (dto: UpdateImmersiveThemeDto) => {
      const response = await api.put('/theming/immersive', dto);
      return response.data as ImmersiveThemeResponse;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['immersive-theme'], data);
      applyCssVariables(data.cssVariables, data.themeType);
      setPreviewState(null);
      toast.success('Tema atualizado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao atualizar tema');
    },
  });

  const previewThemeMutation = useMutation({
    mutationFn: async (dto: UpdateImmersiveThemeDto) => {
      const response = await api.post('/theming/immersive/preview', dto);
      return response.data as ImmersiveThemeResponse;
    },
    onSuccess: (data) => {
      setPreviewState({
        theme: data.themeType,
        config: data.config as ThemeConfig || null,
        cssVariables: data.cssVariables,
      });
      applyCssVariables(data.cssVariables, data.themeType);
    },
    onError: () => {
      toast.error('Erro ao gerar preview');
    },
  });

  const resetThemeMutation = useMutation({
    mutationFn: async () => {
      await api.delete('/theming/immersive');
    },
    onSuccess: () => {
      queryClient.setQueryData(['immersive-theme'], {
        themeType: ImmersiveThemeType.NONE,
        cssVariables: {},
        reducedMotion: false,
        highContrast: false,
        isPreviewMode: false,
      });
      clearCssVariables();
      setPreviewState(null);
      toast.success('Tema resetado para o padrão');
    },
    onError: () => {
      toast.error('Erro ao resetar tema');
    },
  });

  // ============================================
  // APPLY CSS VARIABLES ON LOAD
  // ============================================

  useEffect(() => {
    if (themeData && !previewState) {
      applyCssVariables(themeData.cssVariables, themeData.themeType);
    }
  }, [themeData, previewState]);

  // ============================================
  // ACTIONS
  // ============================================

  const setTheme = useCallback(async (theme: ImmersiveThemeType, config?: ThemeConfig) => {
    await updateThemeMutation.mutateAsync({
      themeType: theme,
      config,
      reducedMotion: themeData?.reducedMotion || systemReducedMotion,
      highContrast: themeData?.highContrast || false,
    });
  }, [updateThemeMutation, themeData, systemReducedMotion]);

  const updateConfig = useCallback(async (config: ThemeConfig) => {
    if (!themeData) return;
    await updateThemeMutation.mutateAsync({
      themeType: themeData.themeType,
      config,
      colorVariant: themeData.colorVariant || undefined,
      reducedMotion: themeData.reducedMotion,
      highContrast: themeData.highContrast,
    });
  }, [updateThemeMutation, themeData]);

  const setColorVariant = useCallback(async (variant: string) => {
    if (!themeData) return;
    await updateThemeMutation.mutateAsync({
      themeType: themeData.themeType,
      config: themeData.config as ThemeConfig,
      colorVariant: variant,
      reducedMotion: themeData.reducedMotion,
      highContrast: themeData.highContrast,
    });
  }, [updateThemeMutation, themeData]);

  const setAccessibility = useCallback(async (reducedMotion: boolean, highContrast: boolean) => {
    if (!themeData) return;
    await updateThemeMutation.mutateAsync({
      themeType: themeData.themeType,
      config: themeData.config as ThemeConfig,
      colorVariant: themeData.colorVariant || undefined,
      reducedMotion,
      highContrast,
    });
  }, [updateThemeMutation, themeData]);

  const previewTheme = useCallback(async (theme: ImmersiveThemeType, config?: ThemeConfig) => {
    await previewThemeMutation.mutateAsync({
      themeType: theme,
      config,
    });
  }, [previewThemeMutation]);

  const resetTheme = useCallback(async () => {
    await resetThemeMutation.mutateAsync();
  }, [resetThemeMutation]);

  const applyPreview = useCallback(async () => {
    if (!previewState) return;
    await updateThemeMutation.mutateAsync({
      themeType: previewState.theme,
      config: previewState.config || undefined,
      reducedMotion: themeData?.reducedMotion || systemReducedMotion,
      highContrast: themeData?.highContrast || false,
    });
  }, [previewState, updateThemeMutation, themeData, systemReducedMotion]);

  const cancelPreview = useCallback(() => {
    setPreviewState(null);
    if (themeData) {
      applyCssVariables(themeData.cssVariables, themeData.themeType);
    } else {
      clearCssVariables();
    }
  }, [themeData]);

  const getPreset = useCallback((type: ImmersiveThemeType): ThemePreset | undefined => {
    return presetsData?.presets.find(p => p.type === type);
  }, [presetsData]);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const contextValue = useMemo<ImmersiveThemeContextValue>(() => ({
    // Estado atual (preview tem prioridade)
    theme: previewState?.theme || themeData?.themeType || ImmersiveThemeType.NONE,
    config: previewState?.config || (themeData?.config as ThemeConfig) || null,
    colorVariant: themeData?.colorVariant || null,
    cssVariables: previewState?.cssVariables || themeData?.cssVariables || {},
    
    // Acessibilidade
    reducedMotion: themeData?.reducedMotion || systemReducedMotion,
    highContrast: themeData?.highContrast || false,
    
    // Estado de carregamento
    isLoading: isLoadingTheme || updateThemeMutation.isPending || previewThemeMutation.isPending,
    isPreviewMode: !!previewState,
    
    // Ações
    setTheme,
    updateConfig,
    setColorVariant,
    setAccessibility,
    previewTheme,
    resetTheme,
    applyPreview,
    cancelPreview,
    
    // Presets
    presets: presetsData?.presets || [],
    getPreset,
  }), [
    previewState,
    themeData,
    systemReducedMotion,
    isLoadingTheme,
    updateThemeMutation.isPending,
    previewThemeMutation.isPending,
    setTheme,
    updateConfig,
    setColorVariant,
    setAccessibility,
    previewTheme,
    resetTheme,
    applyPreview,
    cancelPreview,
    presetsData,
    getPreset,
  ]);

  return (
    <ImmersiveThemeContext.Provider value={contextValue}>
      {children}
    </ImmersiveThemeContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

export const useImmersiveTheme = (): ImmersiveThemeContextValue => {
  const context = useContext(ImmersiveThemeContext);
  if (!context) {
    throw new Error('useImmersiveTheme must be used within an ImmersiveThemeProvider');
  }
  return context;
};

// ============================================
// UTILITY HOOKS
// ============================================

/**
 * Hook to check if a specific theme is active
 */
export const useIsThemeActive = (theme: ImmersiveThemeType): boolean => {
  const { theme: currentTheme } = useImmersiveTheme();
  return currentTheme === theme;
};

/**
 * Hook to get theme-specific class names
 */
export const useThemeClasses = (baseClass: string): string => {
  const { theme } = useImmersiveTheme();
  
  const themeClassMap: Record<ImmersiveThemeType, string> = {
    [ImmersiveThemeType.NONE]: '',
    [ImmersiveThemeType.NEUMORPHISM]: 'theme-neumorphism',
    [ImmersiveThemeType.BRUTALIST]: 'theme-brutalist',
    [ImmersiveThemeType.RETRO_TERMINAL]: 'theme-retro-terminal',
    [ImmersiveThemeType.ORGANIC_NATURE]: 'theme-organic-nature',
    [ImmersiveThemeType.LUXURY_GOLD]: 'theme-luxury-gold',
  };
  
  const themeClass = themeClassMap[theme];
  return themeClass ? `${baseClass} ${themeClass}` : baseClass;
};

/**
 * Hook to get CSS variable value
 */
export const useThemeCssVar = (varName: string, fallback: string = ''): string => {
  const { cssVariables } = useImmersiveTheme();
  return cssVariables[varName] || fallback;
};

export default ImmersiveThemeContext;
